import type { UserId } from '../users/types'
import type { ReservationId, Reservation } from '../reservations/types'
import type { FacilityId, FacilityDetails } from '../facilities/types'
import type { ProgramId } from '../programs/types'
import type { TimetableId } from '../timetables/types'
import type { AreaId } from '../areas/types'

export type PointId = number

export enum PointType {
  PURCHASED = 1,
  FREE = 2,
  CORPORATE = 3,
}

export enum PointStatus {
  VALID = 10,
  INVALID = 20,
}

export enum PointCausedBy {
  UNKNOWN = 'unknown',
  PURCHASED_BULK = 'purchased_bulk',
  PURCHASED_SHORTAGE = 'purchased_shortage',
  PURCHASED_BULK_BONUS = 'purchased_bulk_bonus',
  CORPORATE = 'corporate',
  CORPORATE_BONUS = 'corporate_bonus',
  SUBSCRIPTION = 'subscription',
  SUBSCRIPTION_BONUS = 'subscription_bonus',
  BY_ADMIN = 'by_admin',
  CAMPAIGN = 'campaign',
  POINT_RETURN = 'point_return',
  REFERRAL = 'referral',
  LINE_FRIEND_BONUS = 'line_friend_bonus',
}

export interface Point {
  id: PointId
  amount: number
  balance: number
  causedBy: PointCausedBy
  userId: UserId
  type: PointType
  status: PointStatus
  expirationDate: string
}

export type PointLogId = number

export enum PointLogType {
  FREE = 'free',
  PURCHASED = 'purchased',
  CANCEL = 'cancel',
  RESERVATION = 'reservation',
  REFUND = 'refund',
  CORPORATE = 'corporate',
  EXPIRED = 'expired',
  DISABLE = 'disable',
}

export enum PointLogReason {
  UNKNOWN = 'unknown',
  NEW_MEMBERSHIP_CAMPAIGN = 'new_membership_campaign',
  INVITE_CAMPAIGN = 'invite_campaign',
  REFERRAL_ON_PROFILE_FULFILMENT_FOR_TARGET = 'referral_on_profile_fulfilment_for_target',
  REFERRAL_ON_FIRST_PROGRAM_START_FOR_TARGET = 'referral_on_first_program_start_for_target',
  REFERRAL_ON_FIRST_PROGRAM_START_FOR_SUBJECT = 'referral_on_first_program_start_for_subject',
  BY_ADMIN = 'by_admin',
  BULK_PURCHASE_BONUS = 'bulk_purchase_bonus',
  POINT_RETURN = 'point_return',
  ADMISSION_REWARD_FOR_CORPORATE_MEMBER = 'admission_reward_for_corporate_member',
  MONTHLY_GIVING_CORPORATE_POINT = 'monthly_giving_corporate_point',
  MICO_CLOUD_LINE_FRIEND_BONUS = 'mico_cloud_line_friend_bonus',
  POINT_SUBSCRIPTION = 'point_subscription',
  SUBSCRIPTION_PLAN_UPGRADE_DIFF = 'subscription_plan_upgrade_diff',
  POINT_PURCHASE_BULK = 'point_purchase_bulk',
  POINT_PURCHASE_SHORTAGE = 'point_purchase_shortage',
  LOST_BY_CANCEL_POINT_SUBSCRIPTION = 'lost_by_cancel_point_subscription',
}

export interface PointLog {
  id: PointLogId
  user_id: UserId
  point: number
  type: PointLogType
  reason: PointLogReason
  createdAt: string
  updatedAt: string
  reservationId: ReservationId | null
  reservation: null | {
    id: ReservationId
    facilityId: FacilityId
    programId: ProgramId
    timetableId: TimetableId
    userId: UserId
    point: Reservation['point']
    deletedAt: string
    createdAt: string
    updatedAt: string
    facility: {
      id: FacilityId
      areaId: AreaId
      name: FacilityDetails['name']
      description: FacilityDetails['description']
      tel: FacilityDetails['tel']
      distance: FacilityDetails['distance']
      station: FacilityDetails['station']
      homepage: FacilityDetails['homepage']
      businessHour: FacilityDetails['businessHour']
      attention: FacilityDetails['attention']
      access: FacilityDetails['access']
      sexLimitation: FacilityDetails['sexLimitation']

      // FIXME: 現時点でこのエンドポイントでしか取得できない？ので一旦ここに置いておく。
      postalCode: string
      prefecture: string
      address1: string
      address2: string
      deletedAt: string
      createdAt: string
      updatedAt: string
    }
  }
}

export interface TotalPoint {
  point: number
}

export interface TotalPointParams {
  useDateTime: string
}

export interface PointOnSale {
  value: number
  returnRate: number
  returnValue: number
  inSpecialPeriod: boolean
  packName: string
  packDescription: string
  isFirstRunLimited: boolean
  expirationDateNum: number
}
